// Anonymous listing-draft persistence. Mirrors welcomePrefs.ts.
//
// We persist the ListPropertyNew form state in localStorage so signed-out
// owners can fill out the entire wizard, then auth only at the Publish
// step. Photos/videos are tracked as filename+size+lastModified hints so
// the UI can warn the user if their browser dropped the actual File refs
// (e.g. after a tab close).

import type { BedroomDetail } from "@/lib/bedrooms";

const KEY = "mst_draft_listing_v1";

export interface DraftListing {
  // Step 0: Basics
  title?: string;
  area?: string;
  type?: string;
  bedrooms?: string;
  bathrooms?: string;
  max_guests?: string;
  walking_distance_to_beach_min?: string;

  // Step 0: Owner contact (captured up front; reused for the account later)
  owner_first_name?: string;
  owner_last_name?: string;
  owner_email?: string;
  owner_whatsapp?: string; // national digits only
  owner_country_iso?: string; // e.g. "CR", dial code derived from this
  // Stable id for the server-side lead row, so debounced captures + the
  // publish-time conversion update the same record.
  lead_token?: string;

  // Step 1: Bedroom details
  bedroom_details?: BedroomDetail[];

  // Step 2: Location
  lat?: number;
  lng?: number;

  // Step 3: Pricing & availability
  price?: string;
  price_max?: string;
  price_varies_by_stay?: boolean;
  min_rental_months?: string;
  deposit_months?: string;
  unavailable_dates?: string[];
  availability_mode?: "open" | "window";
  available_from?: string;
  available_until?: string;

  // Step 4: Amenities & services
  amenities?: string[];
  amenities_absent?: string[];
  services?: string[];
  cleaning_days_per_week?: string;
  parking_spots?: string;

  // Step 5: Rules
  pet_friendly?: boolean | null;
  smoking?: boolean | null;

  // Step 7: Description
  description?: string;

  // Step 6: may we use the photos in ads? `_at` is set once answered (yes
  // or no), so "no" and "never asked" differ.
  photo_social_consent?: boolean;
  photo_social_consent_at?: string | null;

  // The saved listing this device draft belongs to, so a returning owner
  // updates it instead of creating a second copy.
  draft_id?: string;

  // Wizard position so a returning user lands where they left off: the step
  // key since Oct 26 (the order changed); a step number before.
  stepKey?: string;
  currentStep?: number;

  // Amenities the photos showed and the wizard set to Yes (Oct 26).
  amenities_from_photos?: string[];

  // Last-edited timestamp (ms) so we can show "saved 2 mins ago" or expire
  // very old drafts.
  updatedAt?: number;
}

export const loadDraftListing = (): DraftListing | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DraftListing;
  } catch {
    return null;
  }
};

export const saveDraftListing = (patch: Partial<DraftListing>): DraftListing => {
  const current = loadDraftListing() ?? {};
  const next: DraftListing = { ...current, ...patch, updatedAt: Date.now() };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }
  return next;
};

export const clearDraftListing = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

export const hasDraftListing = (): boolean => {
  const d = loadDraftListing();
  if (!d) return false;
  // A draft is meaningful if it has at least the property's name or area.
  return !!(d.title || d.area || d.description);
};

// One-shot flag set right before a guest is sent through the Google OAuth
// redirect from the Publish step. On return to /list-property/new we read +
// clear it to know we should auto-publish the (re-hydrated) draft.
